import type { Ref } from "vue";

/**
 * Мост к Angular-приложению, которое живёт в iframe на роутах /settings,
 * /calendar и /storage (см. AngularEmbedPage.vue).
 *
 * Нужен там, где Vue уже находится на нужном роуте и router.push ничего не
 * сделает: iframe не перезагружается, а переключить что-то внутри Angular надо.
 *
 * Мост двусторонний. Обратное направление (Angular → Vue) устроено как
 * объявление состояния, а не как событие смены: гость сообщает «мой язык
 * сейчас такой». Поэтому обработчики обязаны быть идемпотентными — одно и то
 * же объявление приходит и на старте гостя, и после каждой перезагрузки
 * iframe (а она случается на каждом переходе /settings ↔ /calendar ↔ /storage).
 *
 * ВАЖНО: в сообщениях Angular → Vue не передаём ничего, кроме кодов состояния.
 * /ang сейчас встраивается кем угодно (frame-ancestors не отдаётся), поэтому
 * всё, что проедет по этому каналу, доступно и стороннему сайту, который
 * встроит Настройки и поздоровается.
 */

const ANGULAR_URL = process.env.VUE_APP_ANGULAR_URL || "";

/**
 * Разделы страницы Настроек — значения PageTypeEnum из Angular-проекта
 * (src/app/models/model.Address/Inner/model.PageTypeEnum.ts).
 */
export const SETTINGS_SECTION_PROFILE = 5;

/** Версия протокола: сообщения с чужой версией игнорируются обеими сторонами. */
export const PROTOCOL_VERSION = 1;

export type AngularCommand =
    | { type: "settings_section"; section: number }
    | { type: "host_hello"; v: number };

export type AngularEvent =
    | { type: "language_changed"; v: number; language: number; code: string }
    | { type: "session_expired"; v: number };

type AngularEventOf<T extends AngularEvent["type"]> = Extract<AngularEvent, { type: T }>;

type AngularEventHandler = (event: AngularEvent) => void;

/**
 * Храним именно Ref, а не сам элемент: /settings, /calendar и /storage смотрят
 * на один компонент, и переход между ними переиспользует инстанс — onMounted
 * повторно не вызывается, зато содержимое ref остаётся актуальным.
 */
let frameRef: Ref<HTMLIFrameElement | null> | null = null;

export function registerAngularFrame(frame: Ref<HTMLIFrameElement | null>): void {
    frameRef = frame;
}

export function unregisterAngularFrame(frame: Ref<HTMLIFrameElement | null>): void {
    if (frameRef === frame) {
        frameRef = null;
    }
}

/**
 * Origin, на который разрешено слать сообщение. В dev адрес абсолютный и
 * кросс-доменный, в проде — относительный ('/ang'), поэтому вычисляем, а не
 * задаём константой.
 */
function targetOrigin(): string {
    try {
        return new URL(ANGULAR_URL, window.location.origin).origin;
    } catch {
        return window.location.origin;
    }
}

/**
 * Обработчик на стороне Angular парсит строку через JSON.parse, поэтому шлём
 * именно сериализованную строку (тот же формат, что и остальные сообщения).
 */
export function postToAngular(command: AngularCommand): void {
    const contentWindow = frameRef?.value?.contentWindow;
    if (!contentWindow) {
        return;
    }
    contentWindow.postMessage(JSON.stringify(command), targetOrigin());
}

/**
 * Здороваемся, как только гость загрузился. Это единственный способ сообщить
 * ему origin хоста: сам Angular его не знает, а origin у хоста разный по
 * средам (localhost в dev, стенд ветки, прод). Гость запоминает отправителя и
 * отвечает уже ему — поэтому мост не требует никакой конфигурации.
 */
export function sayHelloToAngular(): void {
    postToAngular({ type: "host_hello", v: PROTOCOL_VERSION });
}

const handlers = new Map<string, Set<AngularEventHandler>>();

/** Подписка на событие от гостя. Возвращает функцию отписки. */
export function onAngularEvent<T extends AngularEvent["type"]>(
    type: T,
    handler: (event: AngularEventOf<T>) => void
): () => void {
    let subscribers = handlers.get(type);
    if (!subscribers) {
        subscribers = new Set();
        handlers.set(type, subscribers);
    }

    const stored = handler as AngularEventHandler;
    subscribers.add(stored);
    return () => {
        subscribers?.delete(stored);
    };
}

function onWindowMessage(event: MessageEvent): void {
    const frame = frameRef?.value;
    // Сравнение ссылок на WindowProxy разрешено кросс-доменно и стоит дёшево,
    // поэтому идёт первым: оно отбивает расширения браузера, служебные
    // сообщения и вложенные фреймы (Angular сам встраивает Vue на своих
    // страницах, и его сообщения сюда прилетать не должны).
    if (!frame || event.source !== frame.contentWindow) {
        return;
    }
    if (event.origin !== targetOrigin()) {
        return;
    }
    if (typeof event.data !== "string") {
        return;
    }
    let payload: unknown;
    try {
        payload = JSON.parse(event.data);
    } catch {
        return;
    }
    // JSON.parse("null") возвращает null — без этой проверки чтение поля ниже
    // бросит TypeError прямо внутри обработчика события.
    if (payload === null || typeof payload !== "object") {
        return;
    }
    const message = payload as Record<string, unknown>;
    if (typeof message.type !== "string" || message.v !== PROTOCOL_VERSION) {
        return;
    }
    const subscribers = handlers.get(message.type);
    if (!subscribers) {
        return;
    }
    subscribers.forEach((handler) => handler(payload as AngularEvent));
}

/**
 * Слушатель один на всё приложение. Счётчик ссылок нужен, чтобы повторный
 * подъём (hot-update App.vue) не повесил второй обработчик на то же окно.
 */
let listeners = 0;

export function startAngularBridge(): () => void {
    if (listeners === 0) {
        window.addEventListener("message", onWindowMessage);
    }
    listeners += 1;

    let stopped = false;
    return () => {
        if (stopped) {
            return;
        }
        stopped = true;
        listeners -= 1;
        if (listeners === 0) {
            window.removeEventListener("message", onWindowMessage);
        }
    };
}
